/**
 * Friendship Hook - Manages friendship state and actions
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { friendshipAPI } from '@/lib/api/friendship';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import type { TFunction } from 'i18next';
import type { AxiosError } from 'axios';

/**
 * Maps backend error messages to translation keys
 */
function getTranslatedFriendshipError(
  error: AxiosError<{ detail?: string }>,
  t: TFunction<'common'>
): string {
  const detail = error.response?.data?.detail ?? '';

  // Map backend error messages to translation keys
  if (detail.includes('Already friends')) {
    return t('friendship.errors.alreadyFriends');
  }
  if (detail.includes('already pending') || detail.includes('request already pending')) {
    return t('friendship.errors.requestPending');
  }
  if (detail.includes('already sent you a request')) {
    return t('friendship.errors.characterRequestPending');
  }
  if (detail.includes('Cannot send friendship request to creator')) {
    return t('friendship.errors.creatorCharacter');
  }
  if (detail.includes('Cannot send friendship request at this time')) {
    return t('friendship.errors.cannotRequest');
  }
  if (detail.includes('Character not found')) {
    return t('friendship.errors.notFound');
  }
  if (detail.includes('Friendship feature is disabled')) {
    return t('friendship.errors.featureDisabled');
  }
  if (detail.includes('Not enough coins')) {
    // Extract numbers from the message: "Not enough coins. Need X, have Y"
    const regex = /Need\s+(\d+),\s+have\s+(\d+)/i;
    const match = regex.exec(detail);
    if (match) {
      return t('friendship.errors.notEnoughCoins', {
        needed: match[1],
        have: match[2],
      });
    }
    return t('friendship.toast.not_enough_coins');
  }

  // Fallback to generic error
  return t('friendship.toast.requestFailed');
}

export function useFriendship(characterId: number | undefined) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const { user } = useAuth();

  // Query friendship status (only when authenticated)
  const {
    data: status,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['friendship', characterId],
    queryFn: () => friendshipAPI.getStatus(characterId!).then((res) => res.data),
    enabled: !!characterId && !!user,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Request friendship mutation
  const requestMutation = useMutation({
    mutationFn: (message?: string) => friendshipAPI.request(characterId!, message),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['friendship', characterId] });
      queryClient.invalidateQueries({ queryKey: ['friends'] });
      if (response.data.auto_accepted) {
        toast.success(t('friendship.toast.accepted'));
      } else {
        toast.success(t('friendship.toast.requested'));
      }
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const errorMessage = getTranslatedFriendshipError(error, t);
      toast.error(errorMessage);
    },
  });

  // Accept friendship mutation
  const acceptMutation = useMutation({
    mutationFn: () => friendshipAPI.accept(characterId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friendship', characterId] });
      queryClient.invalidateQueries({ queryKey: ['friends'] });
      queryClient.invalidateQueries({ queryKey: ['pendingRequests'] });
      toast.success(t('friendship.toast.accepted'));
    },
    onError: () => {
      toast.error(t('friendship.toast.acceptFailed'));
    },
  });

  // Reject friendship mutation
  const rejectMutation = useMutation({
    mutationFn: () => friendshipAPI.reject(characterId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friendship', characterId] });
      queryClient.invalidateQueries({ queryKey: ['pendingRequests'] });
      toast.success(t('friendship.toast.rejected'));
    },
    onError: () => {
      toast.error(t('friendship.toast.rejectFailed'));
    },
  });

  // End friendship mutation
  const endMutation = useMutation({
    mutationFn: () => friendshipAPI.end(characterId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friendship', characterId] });
      queryClient.invalidateQueries({ queryKey: ['friends'] });
      toast.success(t('friendship.toast.ended'));
    },
    onError: () => {
      toast.error(t('friendship.toast.endFailed'));
    },
  });

  return {
    // Status
    status,
    isLoading,
    error,
    isFriend: status?.is_friend ?? false,
    isPendingFromUser: status?.status === 'pending_from_user',
    isPendingFromCharacter: status?.status === 'pending_from_character',
    canRequest: status?.can_request ?? true,
    requestCostCoins: status?.request_cost_coins ?? 50,
    featureEnabled: status?.feature_enabled ?? true,

    // Actions
    requestFriendship: requestMutation.mutate,
    acceptFriendship: acceptMutation.mutate,
    rejectFriendship: rejectMutation.mutate,
    endFriendship: endMutation.mutate,

    // Loading states
    isRequesting: requestMutation.isPending,
    isAccepting: acceptMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isEnding: endMutation.isPending,
  };
}

/**
 * Hook for pending friendship requests
 */
export function usePendingFriendRequests() {
  return useQuery({
    queryKey: ['pendingRequests'],
    queryFn: () => friendshipAPI.getPending().then((res) => res.data),
    staleTime: 30 * 1000,
  });
}
